import RotateLeftIcon from '@mui/icons-material/RotateLeft'
import RotateRightIcon from '@mui/icons-material/RotateRight'
import ZoomInIcon from '@mui/icons-material/ZoomIn'
import ZoomOutIcon from '@mui/icons-material/ZoomOut'
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Grid } from '@mui/material'
import { GreenText } from 'components'
import { useEffect, useRef, useState } from 'react'

interface ViewTitleApplicationDeliveryPhotoDialogProps {
  onClose: () => void
  open: boolean
  src: string
}

export default function ViewTitleApplicationDeliveryPhotoDialog({
  onClose,
  open,
  src
}: ViewTitleApplicationDeliveryPhotoDialogProps) {
  const [rotation, setRotation] = useState<number>(0)
  const [zoom, setZoom] = useState<number>(1)

  const rotateLeft = () => setRotation(prev => prev - 90)
  const rotateRight = () => setRotation(prev => prev + 90)
  const zoomIn = () => setZoom(prev => prev + 0.2)
  const zoomOut = () => setZoom(prev => Math.max(1, prev - 0.2))

  const [isDragging, setIsDragging] = useState(false)
  const [originX, setOriginX] = useState(0)
  const [originY, setOriginY] = useState(0)
  const [translateX, setTranslateX] = useState(0)
  const [translateY, setTranslateY] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const imageRef = useRef<HTMLImageElement>(null)

  const handleMouseDown = (e: React.MouseEvent<HTMLImageElement>) => {
    if (zoom > 1) {
      e.preventDefault()
      setIsDragging(true)
      setOriginX(e.clientX)
      setOriginY(e.clientY)
    }
  }

  const handleMouseMove = (e: React.MouseEvent<HTMLImageElement>) => {
    if (isDragging) {
      const deltaX = e.clientX - originX
      const deltaY = e.clientY - originY
      let newTranslateX = translateX + deltaX
      let newTranslateY = translateY + deltaY

      if (containerRef.current && imageRef.current) {
        const containerRect = containerRef.current.getBoundingClientRect()

        const imageWidth = imageRef.current.offsetWidth * zoom
        const imageHeight = imageRef.current.offsetHeight * zoom

        const maxTranslateX = (imageWidth - containerRect.width) / 2
        const maxTranslateY = (imageHeight - containerRect.height) / 2

        const padding = 100

        newTranslateX = Math.max(-maxTranslateX - padding, Math.min(newTranslateX, maxTranslateX + padding))
        newTranslateY = Math.max(-maxTranslateY - padding, Math.min(newTranslateY, maxTranslateY + padding))

        setTranslateX(newTranslateX)
        setTranslateY(newTranslateY)
      }

      setTranslateX(newTranslateX)
      setTranslateY(newTranslateY)
      setOriginX(e.clientX)
      setOriginY(e.clientY)
    }
  }

  const handleMouseUp = () => {
    setIsDragging(false)
  }

  useEffect(() => {
    setRotation(0)
    setZoom(1)
    setTranslateX(0)
    setTranslateY(0)
  }, [src])

  return (
    <Dialog open={open} onClose={onClose} maxWidth='md' fullWidth>
      <DialogContent>
        <Grid container spacing={2} alignItems='center'>
          <DialogTitle>View Title Application Delivery Photo</DialogTitle>

          <Grid item xs={12}>
            <div
              ref={containerRef}
              style={{
                overflow: 'hidden',
                width: '100%',
                height: '60vh',
                backgroundColor: 'black',
                position: 'relative',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '8px',
                borderRadius: '8px'
              }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseUp}
            >
              <img
                ref={imageRef}
                src={src}
                alt='Title Application Delivery'
                style={{
                  width: '80%',
                  height: '100%',
                  transform: `translate(${translateX}px, ${translateY}px) rotate(${rotation}deg) scale(${zoom})`,
                  transformOrigin: 'center center',
                  cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
                  transition: isDragging ? 'none' : 'transform 0.3s ease'
                }}
                draggable={false}
              />
            </div>

            <Grid container justifyContent='space-between' alignItems='center'>
              <Grid item ml={1.25}>
                <Button endIcon={<ZoomInIcon />} color='primary' onClick={zoomIn} variant='text'>
                  Zoom In
                </Button>
              </Grid>

              <Grid item mr={1.25}>
                <Button endIcon={<ZoomOutIcon />} color='primary' onClick={zoomOut} variant='text'>
                  Zoom Out
                </Button>
              </Grid>
            </Grid>

            <Grid container xs={12} margin='auto' mt={1} justifyContent='center' alignItems='center'>
              <Grid item xs={4} sm='auto'>
                <Button startIcon={<RotateLeftIcon />} color='primary' onClick={rotateLeft} variant='text'>
                  ROTATE LEFT
                </Button>
              </Grid>

              <Grid item xs={4}></Grid>

              <Grid item xs={4} sm='auto'>
                <Button endIcon={<RotateRightIcon />} color='primary' onClick={rotateRight} variant='text'>
                  ROTATE RIGHT
                </Button>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <GreenText variant='outlined'>
              {`**View Title Application Delivery Photo Preview:**

Displays the uploaded image of the Michigan Title Application`}
            </GreenText>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions>
        <Button color='inherit' onClick={onClose} variant='text'>
          CLOSE
        </Button>
      </DialogActions>
    </Dialog>
  )
}
